/* eslint-disable no-param-reassign */
import type { SearchParams } from '@/server/searchParams.ts';
import { URLParameters } from '@/app/common/urlParameters.ts';
import type { JSX } from 'react';
import FilterContainer from './FilterContainer';
import { type ControlProps, ControlType, type Option } from '../../Form';

export enum OPTIONS_SORT {
  NONE = 0,
  ALPHABET = 1,
  NUM,
}

type ResponseObject = {
  [key: string]: number | undefined;
};

export type FormOption = {
  value: number | string | null;
  label?: string | JSX.Element;
  className?: string;
  disabled?: boolean;
  addLabel?: string;
};

export const getMultilistboxValue = (param: string, isNumber: boolean): (number | string)[] => {
  const retArray: (number | string)[] = [];
  if (param) {
    param.split(',').forEach((x) => {
      if (isNumber) {
        retArray.push(parseInt(x, 10));
      } else {
        retArray.push(x);
      }
    });
  }
  if (retArray.length === 1 && retArray[0] === null) return [];

  return retArray.filter((x) => x !== null);
};

export const getContainerHeight = (
  isMobile?: boolean,
  mobileHeight?: number,
  height?: number,
  avoidExplicitHeight?: boolean
) => {
  if (avoidExplicitHeight) return 'auto';
  if (!mobileHeight || !height) return 'auto';
  if (typeof isMobile === 'undefined') return height;
  else return isMobile ? mobileHeight : height;
};

export const isCheckboxField = (targetName: URLParameters) =>
  targetName === URLParameters.FREE_CANCELLATION ||
  targetName === URLParameters.FLASH_SALES ||
  targetName === URLParameters.TOP_SECRET_HOTEL ||
  targetName === URLParameters.FILTER_LOYALTY_DEALS ||
  targetName === URLParameters.BLUE_FLAG ||
  targetName === URLParameters.SUSTAINABILITY_CERTIFICATE ||
  targetName === URLParameters.DIRECT ||
  targetName === URLParameters.MY_PRICE ||
  targetName === URLParameters.PINK_PEOPLE ||
  targetName === URLParameters.BOOK_NOW_PAY_LATER;

export const isListBoxField = (targetName: URLParameters) =>
  targetName === URLParameters.ACCOMODATION_TYPE ||
  targetName === URLParameters.STARS ||
  targetName === URLParameters.MEAL ||
  targetName === URLParameters.RATING ||
  targetName === URLParameters.ACCOMODATION_FACILITIES ||
  targetName === URLParameters.GENERAL_ACCOMODATION_FACILITIES ||
  targetName === URLParameters.FAMILY_ACCOMODATION_FACILITIES ||
  targetName === URLParameters.ACTIVITY_ACCOMODATION_FACILITIES ||
  targetName === URLParameters.WELLNESS_ACCOMODATION_FACILITIES ||
  targetName === URLParameters.FOOD_ACCOMODATION_FACILITIES ||
  targetName === URLParameters.LEISURE ||
  targetName === URLParameters.BEACH_TYPE ||
  targetName === URLParameters.FAUNA ||
  targetName === URLParameters.WATERSPORT ||
  targetName === URLParameters.ENTERTAINMENTS ||
  targetName === URLParameters.WATER_WAVES ||
  targetName === URLParameters.ACCOMODATION_BEACH ||
  targetName === URLParameters.ACCOMODATION_SKILIFT ||
  targetName === URLParameters.ACCOMODATION_DISTRICT ||
  targetName === URLParameters.ACCOMODATION_STATION ||
  targetName === URLParameters.ACCOMODATION_CHAIN ||
  targetName === URLParameters.ACCOMODATION_THEME;

export const isListBoxFieldContainer = (targetName: FilterContainer) =>
  targetName === FilterContainer.PropertyTypeContainer ||
  targetName === FilterContainer.StarsContainer ||
  targetName === FilterContainer.MealContainer ||
  targetName === FilterContainer.RatingContainer ||
  targetName === FilterContainer.FacilitiesContainer ||
  targetName === FilterContainer.BeachTypeContainer ||
  targetName === FilterContainer.ClimateContainer ||
  targetName === FilterContainer.FaunaContainer ||
  targetName === FilterContainer.WatersportContainer ||
  targetName === FilterContainer.EntertainmentsContainer ||
  targetName === FilterContainer.WaterwavesContainer ||
  targetName === FilterContainer.AccommodationBeachContainer ||
  targetName === FilterContainer.AccommodationSkiliftContainer ||
  targetName === FilterContainer.AccommodationDistrictsContainer ||
  targetName === FilterContainer.AccommodationStationsContainer ||
  targetName === FilterContainer.AccommodationChainsContainer ||
  targetName === FilterContainer.AccommodationThemesContainer;

export const getFieldNameFromFilterContainer = (filterContainer: FilterContainer): string => {
  switch (filterContainer) {
    case FilterContainer.PropertyTypeContainer:
      return URLParameters.ACCOMODATION_TYPE;
    case FilterContainer.StarsContainer:
      return URLParameters.STARS;
    case FilterContainer.MealContainer:
      return URLParameters.MEAL;
    case FilterContainer.RatingContainer:
      return URLParameters.RATING;
    case FilterContainer.FacilitiesContainer:
      return URLParameters.ACCOMODATION_FACILITIES;
    case FilterContainer.BeachTypeContainer:
      return URLParameters.BEACH_TYPE;
    case FilterContainer.ClimateContainer:
      return URLParameters.CLIMATE;
    case FilterContainer.FaunaContainer:
      return URLParameters.FAUNA;
    case FilterContainer.WatersportContainer:
      return URLParameters.WATERSPORT;
    case FilterContainer.EntertainmentsContainer:
      return URLParameters.ENTERTAINMENTS;
    case FilterContainer.WaterwavesContainer:
      return URLParameters.WATER_WAVES;
    case FilterContainer.AccommodationChainsContainer:
      return URLParameters.ACCOMODATION_CHAIN;
    case FilterContainer.AccommodationThemesContainer:
      return URLParameters.ACCOMODATION_THEME;
    case FilterContainer.AccommodationSkiliftContainer:
      return URLParameters.ACCOMODATION_SKILIFT;
    case FilterContainer.AccommodationBeachContainer:
      return URLParameters.ACCOMODATION_BEACH;
    case FilterContainer.AccommodationDistrictsContainer:
      return URLParameters.ACCOMODATION_DISTRICT;
    case FilterContainer.AccommodationStationsContainer:
      return URLParameters.ACCOMODATION_STATION;
    default:
      return '';
  }
};

export const updateFilter = ({
  targetName,
  targetValue,
  appliedFilters,
}: {
  targetName: string;
  targetValue: number[];
  appliedFilters: SearchParams;
}) => {
  if (targetValue.length > 0) {
    appliedFilters[targetName] = targetValue.join(',');
  } else {
    delete appliedFilters[targetName];
  }
};

export const clearListbox = ({ targetName, appliedFilters }: { targetName: string; appliedFilters: SearchParams }) => {
  delete appliedFilters[targetName];
};

export const parseAddLabel = ({ withBrackets, count }: { withBrackets?: boolean; count: number | string }): string =>
  withBrackets ? `(${count})` : `${count}`;

const removeBrackets = (string: string) => string.replace('(', '').replace(')', '');

export const parseCountFromAddLabel = (addLabel: string): number => {
  if (!addLabel) return 0;
  return parseInt(removeBrackets(addLabel), 10);
};

export const getAppliedFiltersFromControl = (controlPropsList: ControlProps[]): number =>
  controlPropsList.filter(
    (controlProps) =>
      (controlProps.controls || []).filter((control) => {
        if (control?.control === ControlType.CHECKBOX) {
          return control?.value === true;
        }
        if (control?.control === ControlType.SLIDER) {
          return control?.value?.min !== control?.default?.min || control?.value?.max !== control?.default?.max;
        }
        if (
          control?.control === ControlType.LISTBOX ||
          control?.control === ControlType.EXTERNAL ||
          control?.control === ControlType.SEARCH_LIST
        ) {
          return control?.value && control?.value?.length > 0;
        }
        return false;
      }).length > 0
  ).length || 0;

export const sortOptionsByNum = (options: Option[]) =>
  options.sort((a, b) => {
    const bCount = parseCountFromAddLabel(b?.addLabel || '');
    const aCount = parseCountFromAddLabel(a?.addLabel || '');
    return (Number.isNaN(bCount) ? 0 : bCount) - (Number.isNaN(aCount) ? 0 : aCount);
  });

export const sortOptionsByAlphabetic = (options: Option[]) =>
  options.sort((a, b) =>
    typeof a.label === 'string' && typeof b.label === 'string' ? a.label.localeCompare(b.label) : 0
  );

export const getOptionFromDynamicOptions = (object: ResponseObject): number[] =>
  Object.keys(object).map((accType) => parseInt(accType, 10));

export const getDynamicOptionsOrFallback = (
  dynamicOptions: ResponseObject | undefined,
  fallback: number[]
): number[] => (dynamicOptions ? getOptionFromDynamicOptions(dynamicOptions) : fallback);

export const getOptionFromId = (id: number, options: { id: number; name: string; text: string; analytics: string }[]) =>
  options.find((d) => d.id === id);
